import { cn } from "@/lib/cn";
import * as React from "react";
import IconPrimitive from "./icon";

// `[&_svg:not(...)]:tw-text-[color:inherit]`: the app's global `.tabler-icon` rule sets
// a colour ON the svg, so a stroke-drawn Tabler glyph ignored the button's
// text colour and came out dark navy — invisible on a violet or red button.
// Handing the svg the button's own colour fixes every variant and every
// state at once (the variants below set the colour; disabled and hover carry
// it), and leaves the app's filled icons alone — those take their colour from
// the `[&_svg]:tw-fill-*` rules in the same variants. An icon with its own
// `tw-text-*` class is skipped, so it keeps the colour it asked for.
const BASE_STYLE =
  "tw-inline-flex tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-lg tw-text-sm/normal tw-font-medium tw-transition-colors disabled:tw-cursor-not-allowed !tw-transition-colors !tw-duration-200 [&_svg:not([class*='tw-text-'])]:tw-text-[color:inherit]";

const VARIANTS = {
  default:
    "tw-outline tw-outline-1 tw-outline/5 tw-bg-violet-700 tw-shadow-2xs tw-text-white hover:tw-bg-violet-500 disabled:tw-bg-blue-200 disabled:tw-text-blue-700 [&_svg]:tw-fill-white [&_svg]:disabled:tw-fill-blue-700",
  pro: "tw-bg-pro-base tw-shadow-xs tw-text-white hover:tw-opacity-80 !tw-transition-opacity tw-duration-200 [&_svg]:tw-fill-white",
  team: "tw-border tw-border-solid tw-border-team-border tw-bg-team-base tw-shadow-main tw-text-white hover:tw-opacity-80 !tw-transition-opacity tw-duration-200 [&_svg]:tw-fill-white",
  starter:
    "tw-border tw-border-solid tw-border-starter-border tw-bg-starter-base tw-shadow-main tw-text-white hover:tw-opacity-80 [&_svg]:tw-fill-white tw-transition-all tw-duration-200",
  secondary:
    "tw-outline tw-outline-1 tw-shadow-2xs tw-outline-blue/5 tw-bg-white tw-text-blue-base hover:tw-bg-blue-50/50 disabled:tw-text-blue-200 [&_svg]:tw-fill-blue-base [&_svg]:disabled:tw-fill-blue-200 disabled:hover:tw-bg-white",
  outline:
    "tw-border tw-border-solid tw-border-primary-200 tw-bg-[transparent] tw-text-primary-base hover:tw-border-primary-100 disabled:tw-border-blue-200 disabled:tw-text-blue-700 [&_svg]:tw-fill-primary-base [&_svg]:disabled:tw-fill-blue-700",
  link: "tw-bg-[transparent] tw-text-primary-base disabled:tw-text-blue-700 [&_svg]:tw-fill-primary-base [&_svg]:disabled:tw-fill-blue-700",
  ghost:
    "tw-bg-[transparent] tw-text-primary-base hover:tw-bg-blue-50 disabled:tw-text-blue-700 [&_svg]:tw-fill-primary-base [&_svg]:disabled:tw-fill-blue-700",
  destructive:
    "tw-border tw-border-solid tw-border-[#DC2626] tw-bg-[#DC2626] tw-shadow-main tw-text-white hover:tw-bg-[#D94A5B] disabled:tw-bg-blue-200 disabled:tw-border-blue-300 disabled:tw-text-blue-700 [&_svg]:tw-fill-white [&_svg]:disabled:tw-fill-blue-700",
};

const SIZES = {
  xs: "tw-text-[11px]/normal tw-h-6 tw-px-2 [&_svg]:tw-size-[14px] [&_i]:tw-text-[14px]/none [&_.left]:-tw-ml-[1px] [&_.right]:-tw-mr-[1px]",
  sm: "tw-h-8 tw-text-xs tw-px-3 [&_svg]:tw-size-4 tw-rounded-lg [&_i]:tw-text-[14px]/none [&_.left]:-tw-ml-[2px] [&_.right]:-tw-mr-[2px]",
  default:
    "tw-h-9 tw-px-4 [&_svg]:tw-size-5 [&_i]:tw-text-base/none tw-rounded-lg [&_.left]:-tw-ml-1 [&_.right]:-tw-mr-1",
  md: "tw-h-12 tw-px-6 [&_svg]:tw-size-6 [&_i]:tw-text-xl/none tw-rounded-lg [&_.left]:-tw-ml-1 [&_.right]:-tw-mr-1 tw-text-base",
  lg: "tw-h-12 tw-px-6 [&_svg]:tw-size-6 [&_i]:tw-text-xl/none tw-rounded-lg [&_.left]:-tw-ml-1 [&_.right]:-tw-mr-1",
};

export type ButtonVariant = keyof typeof VARIANTS;
export type ButtonSize = keyof typeof SIZES;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
  isLoading?: boolean;
}

type ButtonIconProps = {
  icon: string | React.ReactNode;
  className?: string;
  position?: "left" | "right";
};

type ButtonRootComponent = React.ForwardRefExoticComponent<
  ButtonProps & React.RefAttributes<HTMLButtonElement>
> & {
  Icon: React.FC<ButtonIconProps>;
};

const ButtonIcon: React.FC<ButtonIconProps> = ({
  icon,
  className,
  position = "left",
}) => {
  const wrapperClassName = cn(
    position === "left" ? "tw-mr-[6px]" : "tw-ml-[6px]",
    position === "left" ? "-tw-order-1" : "tw-order-2",
    className,
    position,
  );
  if (typeof icon !== "string") {
    return <span className={wrapperClassName}>{icon}</span>;
  }
  return <IconPrimitive icon={icon} className={wrapperClassName} />;
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "default",
      size = "default",
      asChild = false,
      isLoading = false,
      ...props
    },
    ref,
  ) => {
    "use no memo";
    if (
      asChild &&
      React.isValidElement<{
        className?: string;
        ref?: React.Ref<any>;
        [key: string]: unknown;
      }>(props.children)
    ) {
      type ChildProps =
        typeof props.children extends React.ReactElement<infer P>
          ? P
          : Record<string, unknown>;

      const child = props.children as React.ReactElement<ChildProps>;

      return React.cloneElement(child, {
        className: cn(
          BASE_STYLE,
          VARIANTS[variant],
          SIZES[size],
          className,
          child.props.className,
          isLoading && "tw-cursor-default",
        ),
        ref,
        ...props,
      });
    }

    return (
      <button
        className={cn(
          BASE_STYLE,
          VARIANTS[variant],
          SIZES[size],
          className,
          isLoading && "tw-cursor-default",
        )}
        ref={ref}
        {...props}
      />
    );
  },
) as ButtonRootComponent;

Button.displayName = "Button";
Button.Icon = ButtonIcon;

export default Button;
